import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DataObjectIcon from '@mui/icons-material/DataObject';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PsychologyIcon from '@mui/icons-material/Psychology';
import RefreshIcon from '@mui/icons-material/Refresh';
import SchemaIcon from '@mui/icons-material/Schema';
import StorageIcon from '@mui/icons-material/Storage';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { GetAIObservabilityRun, GetAIObservabilityRuns, GetAIObservabilityThreads } from '../../REST/ai';
import AIQueryResults, { normalizeToolCall } from './AIQueryResults';

const MAX_VISIBLE_CONTEXT_ATOMS = 60;

const statusColor = (status) => {
    if (status === 'completed')
        return 'success';
    if (status === 'failed')
        return 'error';
    if (status === 'skipped' || status === 'incomplete')
        return 'warning';
    if (status === 'running')
        return 'info';
    return 'default';
};

// Category -> MUI palette color + icon, mirroring the reference CATEGORY_STYLES.
const CATEGORY_STYLES = {
    conversation: { color: 'primary.main', label: 'conversation', Icon: ChatBubbleOutlineIcon },
    context: { color: 'secondary.main', label: 'context', Icon: TravelExploreIcon },
    schema_drilldown: { color: 'info.main', label: 'schema', Icon: SchemaIcon },
    data: { color: 'success.main', label: 'data', Icon: DataObjectIcon },
    llm: { color: 'warning.main', label: 'llm', Icon: PsychologyIcon },
    storage: { color: 'text.secondary', label: 'storage', Icon: StorageIcon },
};
const categoryStyle = (category) => CATEGORY_STYLES[category] || { color: 'text.disabled', label: category || 'step', Icon: DataObjectIcon };

const fmtDate = (value) => {
    if (!value)
        return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const fmtDuration = (ms) => {
    if (!ms || ms <= 0)
        return null;
    return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`;
};

// Group flat runs by thread_id (fallback path when the threads endpoint is unavailable).
const groupRunsIntoThreads = (runs = []) => {
    const byThread = new Map();
    runs.forEach((run) => {
        const key = run.thread_id || run.request_id;
        if (!byThread.has(key))
            byThread.set(key, []);
        byThread.get(key).push(run);
    });
    return Array.from(byThread.entries()).map(([threadId, requests]) => {
        const sorted = [...requests].sort((a, b) => new Date(b.started_at) - new Date(a.started_at));
        const latest = sorted[0] || {};
        return {
            thread_id: threadId,
            title: latest.question,
            request_count: sorted.length,
            last_activity: latest.started_at,
            status: latest.status,
            needs_clarification: sorted.some((run) => run.status === 'clarification' || run.needs_clarification),
            requests: sorted,
        };
    }).sort((a, b) => new Date(b.last_activity) - new Date(a.last_activity));
};

// Build a simplified timeline from a streaming (live) trace's SSE stages.
const liveStepsFromTrace = (trace) => {
    if (!trace)
        return [];
    const steps = [];
    if (trace.context) {
        steps.push({
            step_id: 'context',
            name: 'Reporting context',
            category: 'context',
            status: trace.context.error ? 'failed' : 'completed',
            summary: trace.context.error || `${trace.context.collection_count || 0} collections selected`,
            details: trace.context,
        });
    }
    (trace.tool_calls || []).forEach((call, index) => {
        const isSchema = call.name === 'describe_schema' || !!call.data;
        steps.push({
            step_id: `tool-${call.call_id || index}`,
            name: isSchema ? `Schema: ${call.collection || call.name}` : `Data retrieval ${index + 1}`,
            category: isSchema ? 'schema_drilldown' : 'data',
            status: call.result ? (call.result.error ? 'failed' : 'completed') : 'running',
            summary: isSchema
                ? (call.data?.description || 'Schema description')
                : `${call.name || 'query'} - ${call.result?.row_count ?? 0} rows`,
            details: { tool_calls: [call] },
        });
    });
    if (trace.answer || trace.status === 'completed') {
        steps.push({
            step_id: 'llm',
            name: 'Generated answer',
            category: 'llm',
            status: trace.status === 'completed' ? 'completed' : 'running',
            summary: trace.stage || 'responding',
            details: { answer_characters: (trace.answer || '').length },
        });
    }
    return steps;
};

const MetricCard = React.memo(function MetricCard({ label, value, note }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
            <Typography variant="overline" color="text.secondary">{label}</Typography>
            <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 600 }}>{value}</Typography>
            {note && <Typography variant="caption" color="text.secondary">{note}</Typography>}
        </Paper>
    );
});

const KeyValueTable = React.memo(function KeyValueTable({ data }) {
    const rows = Object.entries(data || {}).filter(([, value]) => (
        value != null && value !== '' && !(Array.isArray(value) && value.length === 0)
    ));
    if (rows.length === 0)
        return null;
    return (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <Table size="small">
                <TableBody>
                    {rows.map(([key, value]) => (
                        <TableRow key={key}>
                            <TableCell sx={{ width: 180, bgcolor: 'action.hover', fontWeight: 500, verticalAlign: 'top' }}>
                                {key.replace(/_/g, ' ')}
                            </TableCell>
                            <TableCell sx={{ overflowWrap: 'anywhere', verticalAlign: 'top' }}>
                                {typeof value === 'object' ? (
                                    <Box component="pre" sx={{ m: 0, fontSize: 11, whiteSpace: 'pre-wrap' }}>
                                        {JSON.stringify(value, null, 2)}
                                    </Box>
                                ) : String(value)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Paper>
    );
});

// Parse a context atom ("collection:invoices", "field:invoices.status",
// "metric:invoice_status_count", "rel:invoices→documents", "template:...") and
// group them so 40+ atoms are scannable instead of a flat wall of chips.
const ATOM_KIND_COLOR = { collection: 'primary', metric: 'secondary', template: 'success', rel: 'default', other: 'default' };

function groupAtoms(atoms) {
    const collections = new Map();
    const buckets = { metric: [], template: [], rel: [], other: [] };
    const ensureColl = (name) => {
        if (!collections.has(name))
            collections.set(name, { name, kind: 'collection', items: [] });
        return collections.get(name);
    };
    atoms.forEach((atom) => {
        const idx = atom.indexOf(':');
        const type = idx >= 0 ? atom.slice(0, idx) : '';
        const body = idx >= 0 ? atom.slice(idx + 1) : atom;
        if (type === 'collection') {
            ensureColl(body);
        } else if (type === 'field') {
            const coll = body.split('.')[0];
            const label = body.slice(coll.length + 1) || body;
            ensureColl(coll).items.push({ label, full: atom });
        } else if (buckets[type]) {
            buckets[type].push({ label: body, full: atom });
        } else {
            buckets.other.push({ label: atom, full: atom });
        }
    });
    const groups = Array.from(collections.values()).sort((a, b) => a.name.localeCompare(b.name));
    if (buckets.metric.length) groups.push({ name: 'Metrics', kind: 'metric', items: buckets.metric });
    if (buckets.template.length) groups.push({ name: 'Templates', kind: 'template', items: buckets.template });
    if (buckets.rel.length) groups.push({ name: 'Relationships', kind: 'rel', items: buckets.rel });
    if (buckets.other.length) groups.push({ name: 'Other', kind: 'other', items: buckets.other });
    return groups;
}

const GroupedAtoms = React.memo(function GroupedAtoms({ atoms }) {
    const groups = useMemo(() => groupAtoms(atoms.slice(0, MAX_VISIBLE_CONTEXT_ATOMS * 4)), [atoms]);
    return (
        <Stack spacing={1}>
            {groups.map((group) => (
                <Box key={`${group.kind}-${group.name}`}>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                        {group.name}{group.items.length ? ` (${group.items.length})` : ''}
                    </Typography>
                    {group.items.length > 0 ? (
                        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                            {group.items.map((item) => (
                                <Chip
                                    key={item.full}
                                    size="small"
                                    variant="outlined"
                                    color={ATOM_KIND_COLOR[group.kind]}
                                    label={item.label}
                                    title={item.full}
                                />
                            ))}
                        </Stack>
                    ) : (
                        <Chip size="small" variant="outlined" color="primary" label="collection" />
                    )}
                </Box>
            ))}
        </Stack>
    );
});

// Render prior conversation turns supplied to the LLM as readable chat bubbles.
const ConversationHistory = React.memo(function ConversationHistory({ turns }) {
    return (
        <Stack spacing={1}>
            {turns.map((turn, index) => {
                const sender = turn.sender || 'user';
                const isUser = sender === 'user';
                return (
                    <Box key={index} sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
                        <Paper
                            variant="outlined"
                            sx={{ px: 1.5, py: 1, maxWidth: '85%', bgcolor: isUser ? 'action.hover' : 'background.paper' }}
                        >
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', textTransform: 'uppercase', fontWeight: 600, fontSize: 9 }}
                            >
                                {sender}
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                                {turn.content}
                            </Typography>
                        </Paper>
                    </Box>
                );
            })}
        </Stack>
    );
});

const ContextDetails = React.memo(function ContextDetails({ details }) {
    const atoms = details?.selected_atoms || details?.atoms || [];
    return (
        <Stack spacing={1.5}>
            {details?.error && <Alert severity="error">{details.error}</Alert>}
            {atoms.length > 0 && (
                <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                        Selected atoms ({atoms.length})
                    </Typography>
                    <GroupedAtoms atoms={atoms} />
                </Box>
            )}
            <KeyValueTable data={{
                collection_count: details?.collection_count,
                selection_method: details?.selection_method,
                embedding_source: details?.embedding_source,
                top_scores: details?.top_scores,
            }} />
        </Stack>
    );
});

// Render the data passed through a single timeline step, by category.
const StepDetails = React.memo(function StepDetails({ step }) {
    const { category, details } = step;
    if (!details)
        return <Typography variant="body2" color="text.secondary">No additional detail recorded.</Typography>;

    // Conversation-history step: show the actual prior turns, not just a count.
    if (details.turns !== undefined || details.history_count !== undefined) {
        const turns = Array.isArray(details.turns) ? details.turns : [];
        if (turns.length > 0)
            return <ConversationHistory turns={turns} />;
        return (
            <Typography variant="body2" color="text.secondary">
                No earlier messages — this was the first message in the thread.
            </Typography>
        );
    }

    if (category === 'context')
        return <ContextDetails details={details} />;

    if (category === 'data' || category === 'schema_drilldown') {
        const toolCalls = (details.tool_calls || []).map(normalizeToolCall);
        if (toolCalls.length === 0)
            return <KeyValueTable data={details} />;
        return <AIQueryResults toolCalls={toolCalls} />;
    }

    return <KeyValueTable data={details} />;
});

const TimelineStep = React.memo(function TimelineStep({ step, isLast }) {
    const [open, setOpen] = useState(false);
    const style = categoryStyle(step.category);
    const { Icon } = style;
    const duration = fmtDuration(step.duration_ms);
    const hasDetails = !!step.details;

    return (
        <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                <Box
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: style.color,
                        border: 2,
                        borderColor: style.color,
                        bgcolor: 'background.paper',
                    }}
                >
                    <Icon fontSize="small" />
                </Box>
                {!isLast && <Box sx={{ flex: 1, width: 2, bgcolor: 'divider', my: 0.5, minHeight: 12 }} />}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, pb: isLast ? 0 : 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{step.name}</Typography>
                    <Chip size="small" variant="outlined" label={style.label} sx={{ borderColor: style.color, color: style.color }} />
                    <Chip size="small" color={statusColor(step.status)} label={step.status} />
                    {duration && <Typography variant="caption" color="text.secondary">{duration}</Typography>}
                </Stack>
                {step.summary && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {step.summary}
                    </Typography>
                )}
                {hasDetails && (
                    <>
                        <Button
                            size="small"
                            sx={{ mt: 0.5, px: 0.5, minWidth: 0 }}
                            startIcon={open ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                            onClick={() => setOpen((value) => !value)}
                        >
                            {open ? 'Hide details' : 'View details'}
                        </Button>
                        <Collapse in={open} unmountOnExit>
                            <Box sx={{ mt: 1 }}>
                                <StepDetails step={step} />
                            </Box>
                        </Collapse>
                    </>
                )}
            </Box>
        </Box>
    );
});

const Timeline = React.memo(function Timeline({ steps }) {
    if (!steps || steps.length === 0) {
        return (
            <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                No timeline steps were recorded for this request.
            </Typography>
        );
    }
    return (
        <Box>
            {steps.map((step, index) => (
                <TimelineStep
                    key={step.step_id || `${step.name}-${index}`}
                    step={step}
                    isLast={index === steps.length - 1}
                />
            ))}
        </Box>
    );
});

function AIObservability({ active, liveTraces = [], refreshKey }) {
    const [threads, setThreads] = useState([]);
    const [selectedThreadId, setSelectedThreadId] = useState(null);
    const [selectedRequestId, setSelectedRequestId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [error, setError] = useState(null);
    const [note, setNote] = useState('');
    const loadedRefreshKey = useRef(null);

    // Map live (streaming) traces by request_id for merging onto persisted requests.
    const liveByRequest = useMemo(() => {
        const map = new Map();
        liveTraces.forEach((trace) => {
            const requestId = trace.request_id || trace.trace_id;
            if (requestId)
                map.set(requestId, trace);
        });
        return map;
    }, [liveTraces]);

    // Merge live traces (as synthetic thread/request summaries) over persisted threads.
    const mergedThreads = useMemo(() => {
        const liveRequests = liveTraces.map((trace) => ({
            request_id: trace.request_id || trace.trace_id,
            thread_id: trace.thread_id,
            question: trace.question,
            started_at: trace.started_at,
            status: trace.status,
            operational_data_access: (trace.tool_calls || []).length ? 'performed' : 'none',
            needs_clarification: trace.status === 'clarification',
            source: 'live',
        }));
        if (liveRequests.length === 0)
            return threads;

        const merged = threads.map((thread) => ({ ...thread, requests: [...(thread.requests || [])] }));
        liveRequests.forEach((req) => {
            const key = req.thread_id || req.request_id;
            let thread = merged.find((item) => item.thread_id === key);
            if (!thread) {
                thread = { thread_id: key, title: req.question, request_count: 0, last_activity: req.started_at, status: req.status, needs_clarification: false, requests: [] };
                merged.unshift(thread);
            }
            const existing = thread.requests.findIndex((item) => item.request_id === req.request_id);
            if (existing >= 0)
                thread.requests[existing] = { ...thread.requests[existing], ...req };
            else
                thread.requests.unshift(req);
            thread.title = req.question || thread.title;
            thread.last_activity = req.started_at;
            thread.status = req.status;
            thread.request_count = thread.requests.length;
            if (req.needs_clarification)
                thread.needs_clarification = true;
        });
        return merged.sort((a, b) => new Date(b.last_activity) - new Date(a.last_activity));
    }, [threads, liveTraces]);

    const selectedThread = mergedThreads.find((thread) => thread.thread_id === selectedThreadId)
        || mergedThreads[0]
        || null;
    const requests = useMemo(() => selectedThread?.requests || [], [selectedThread]);
    const selectedRequest = requests.find((req) => req.request_id === selectedRequestId)
        || requests[0]
        || null;
    const activeRequestId = selectedRequest?.request_id;
    const liveTrace = activeRequestId ? liveByRequest.get(activeRequestId) : null;

    const loadThreads = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            let nextThreads = [];
            let nextNote = '';
            try {
                const response = await GetAIObservabilityThreads();
                if (response.data?.available && (response.data.threads || []).length) {
                    nextThreads = response.data.threads;
                    nextNote = response.data.note || '';
                }
            }
            catch {
                // fall through to runs-based grouping
            }
            if (nextThreads.length === 0) {
                const runsResponse = await GetAIObservabilityRuns();
                nextThreads = groupRunsIntoThreads(runsResponse.data?.traces || []);
                nextNote = runsResponse.data?.note || '';
            }
            setThreads(nextThreads);
            setNote(nextNote);
            setSelectedThreadId((current) => current || nextThreads[0]?.thread_id || null);
        }
        catch (err) {
            setError(err.response?.data?.message || err.message || 'Could not load observability threads');
        }
        finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!active || loadedRefreshKey.current === refreshKey)
            return;
        loadedRefreshKey.current = refreshKey;
        loadThreads();
    }, [active, loadThreads, refreshKey]);

    // Auto-select the first request when the thread changes.
    useEffect(() => {
        if (requests.length === 0) {
            setSelectedRequestId(null);
            return;
        }
        const stillExists = requests.some((req) => req.request_id === selectedRequestId);
        if (!stillExists)
            setSelectedRequestId(requests[0].request_id);
    }, [requests, selectedRequestId]);

    // Fetch the persisted trace detail for the selected request.
    useEffect(() => {
        if (!active || !activeRequestId) {
            setDetail(null);
            return undefined;
        }
        let cancelled = false;
        setDetailLoading(true);
        GetAIObservabilityRun(activeRequestId)
            .then((response) => {
                if (!cancelled)
                    setDetail(response.data);
            })
            .catch(() => {
                if (!cancelled)
                    setDetail(null);
            })
            .finally(() => {
                if (!cancelled)
                    setDetailLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [active, activeRequestId, refreshKey]);

    const metrics = useMemo(() => {
        const completed = mergedThreads.reduce((total, thread) => (
            total + (thread.requests || []).filter((req) => req.status === 'completed').length
        ), 0);
        const dataRetrievals = mergedThreads.reduce((total, thread) => (
            total + (thread.requests || []).filter((req) => req.operational_data_access === 'performed').length
        ), 0);
        const clarifications = mergedThreads.filter((thread) => thread.needs_clarification).length;
        return { threads: mergedThreads.length, completed, dataRetrievals, clarifications };
    }, [mergedThreads]);

    // Prefer persisted steps; while streaming, fall back to a live timeline.
    const persistedReady = detail?.request_id === activeRequestId && (detail?.steps || []).length > 0;
    const steps = persistedReady ? detail.steps : liveStepsFromTrace(liveTrace);
    const answer = liveTrace?.answer || detail?.answer || selectedRequest?.answer_preview || '';

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 2 }}>
            <Alert
                severity={error ? 'error' : 'info'}
                action={(
                    <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={loadThreads} disabled={loading}>
                        Refresh
                    </Button>
                )}
            >
                {error || note || 'Traces are saved in the RevJet AI metadata database and remain available after refresh.'}
            </Alert>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                <MetricCard label="Threads" value={metrics.threads} />
                <MetricCard label="Completed" value={metrics.completed} />
                <MetricCard label="Data retrievals" value={metrics.dataRetrievals} />
                <MetricCard label="Clarifications" value={metrics.clarifications} />
            </Box>

            <Paper
                variant="outlined"
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: '300px 320px minmax(0, 1fr)' },
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                }}
            >
                {/* Pane 1: Threads */}
                <Box sx={{ overflowY: 'auto', borderRight: { lg: 1 }, borderBottom: { xs: 1, lg: 0 }, borderColor: 'divider' }}>
                    <Box sx={{ px: 2, py: 1.5 }}>
                        <Typography variant="subtitle2">Threads</Typography>
                    </Box>
                    <Divider />
                    {loading && mergedThreads.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={24} /></Box>
                    ) : mergedThreads.length === 0 ? (
                        <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                            No threads yet. Send a chat message first.
                        </Typography>
                    ) : (
                        <List dense disablePadding>
                            {mergedThreads.map((thread) => (
                                <ListItemButton
                                    key={thread.thread_id}
                                    selected={thread.thread_id === selectedThread?.thread_id}
                                    onClick={() => setSelectedThreadId(thread.thread_id)}
                                    sx={{ alignItems: 'flex-start' }}
                                >
                                    <ListItemText
                                        primary={thread.title || 'Untitled thread'}
                                        secondary={(
                                            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 0.5 }}>
                                                {thread.last_activity && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                                                        {fmtDate(thread.last_activity)}
                                                    </Typography>
                                                )}
                                                <Chip size="small" variant="outlined" label={`${thread.request_count} req`} />
                                                <Chip size="small" color={statusColor(thread.status)} label={thread.status || 'unknown'} />
                                                {thread.needs_clarification && (
                                                    <Chip size="small" color="warning" icon={<HelpOutlineIcon />} label="clarification" />
                                                )}
                                            </Stack>
                                        )}
                                        primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 500, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' } }}
                                        secondaryTypographyProps={{ component: 'div' }}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    )}
                </Box>

                {/* Pane 2: Requests */}
                <Box sx={{ overflowY: 'auto', borderRight: { lg: 1 }, borderBottom: { xs: 1, lg: 0 }, borderColor: 'divider' }}>
                    <Box sx={{ px: 2, py: 1.5 }}>
                        <Typography variant="subtitle2">Requests</Typography>
                    </Box>
                    <Divider />
                    {requests.length === 0 ? (
                        <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                            Select a thread to inspect its requests.
                        </Typography>
                    ) : (
                        <List dense disablePadding>
                            {requests.map((req) => (
                                <ListItemButton
                                    key={req.request_id}
                                    selected={req.request_id === selectedRequest?.request_id}
                                    onClick={() => setSelectedRequestId(req.request_id)}
                                    sx={{ alignItems: 'flex-start' }}
                                >
                                    <ListItemText
                                        primary={req.question || 'Untitled request'}
                                        secondary={(
                                            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 0.5 }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                                                    {fmtDate(req.started_at)}
                                                </Typography>
                                                <Chip size="small" color={statusColor(req.status)} label={req.status || 'unknown'} />
                                                {req.operational_data_access === 'performed' && (
                                                    <Chip size="small" variant="outlined" icon={<DataObjectIcon />} label="data" />
                                                )}
                                            </Stack>
                                        )}
                                        primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 500, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' } }}
                                        secondaryTypographyProps={{ component: 'div' }}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    )}
                </Box>

                {/* Pane 3: Timeline */}
                <Box sx={{ overflowY: 'auto', p: { xs: 2, md: 3 } }}>
                    {!selectedRequest ? (
                        <Typography color="text.secondary" sx={{ py: 8, textAlign: 'center' }}>
                            Select a request to inspect its timeline.
                        </Typography>
                    ) : (
                        <Stack spacing={2.5}>
                            <Box>
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                                    <Typography variant="h6">{liveTrace ? 'Live request timeline' : 'Request timeline'}</Typography>
                                    <Chip size="small" color={statusColor(selectedRequest.status)} label={selectedRequest.status || 'unknown'} />
                                    {selectedRequest.needs_clarification && (
                                        <Chip size="small" color="warning" icon={<HelpOutlineIcon />} label="clarification" />
                                    )}
                                </Stack>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    {selectedRequest.question}
                                </Typography>
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
                                    {selectedRequest.request_id && (
                                        <Chip size="small" icon={<DataObjectIcon />} label={selectedRequest.request_id} />
                                    )}
                                    {selectedThread?.thread_id && (
                                        <Chip size="small" icon={<StorageIcon />} label={selectedThread.thread_id} />
                                    )}
                                </Stack>
                            </Box>

                            {detail?.error && <Alert severity="error">{detail.error}</Alert>}

                            <Divider />

                            {detailLoading && !liveTrace ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
                            ) : (
                                <Timeline steps={steps} />
                            )}

                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="overline" color="text.secondary">Answer</Typography>
                                <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap', lineHeight: 1.65 }}>
                                    {answer || 'Waiting for the answer...'}
                                </Typography>
                            </Paper>
                        </Stack>
                    )}
                </Box>
            </Paper>
        </Box>
    );
}

export default React.memo(AIObservability, (previous, next) => {
    if (!previous.active && !next.active)
        return true;
    return previous.active === next.active
        && previous.liveTraces === next.liveTraces
        && previous.refreshKey === next.refreshKey;
});
